import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Coffee, Leaf, Sun, Sunset, Moon, MapPin, Printer, Share2, ArrowUpRight, CalendarDays, Send, Loader2, Check, X } from "lucide-react";
import { API_BASE } from "@/lib/api";

const MONTHS = ["Flexible", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const RequestJourneyPanel = ({ plan, journeyId, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", travel_month: "Flexible", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const inputCls = "w-full h-11 rounded-xl bg-[#F7F5F0] border border-[#E5E2D9] px-4 text-sm focus:outline-none focus:border-[#89937C]";
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_BASE}/journey/${journeyId}/request`, { ...form, phone: form.phone || null, message: form.message || null });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div data-testid="request-journey-panel" className="mt-6 bg-white border border-[#C57B57]/40 rounded-3xl p-7 text-left max-w-lg mx-auto">
      {sent ? (
        <div className="text-center py-4" data-testid="request-journey-success">
          <div className="w-12 h-12 rounded-full bg-[#89937C]/15 flex items-center justify-center mx-auto mb-4"><Check size={20} className="text-[#89937C]" /></div>
          <h4 className="font-serif-display text-xl text-[#2D372B]">Request sent!</h4>
          <p className="text-sm text-[#5C665A] mt-2">Coach Sanjay will reply to {form.email} within 24 hours with availability, refined pricing and next steps.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-serif-display text-xl text-[#2D372B]">Request "{plan.title}"</h4>
            <button type="button" onClick={onClose} aria-label="Close" className="text-[#5C665A] hover:text-[#2D372B]"><X size={17} /></button>
          </div>
          <p className="text-xs text-[#5C665A]">We'll arrange the stays, guides, transfers and experiences — you just show up.</p>
          <input data-testid="request-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} />
          <input data-testid="request-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="request-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="WhatsApp (optional)" className={inputCls} />
            <select data-testid="request-month" value={form.travel_month} onChange={(e) => setForm({ ...form, travel_month: e.target.value })} className={inputCls}>
              {MONTHS.map((m) => <option key={m} value={m}>{m === "Flexible" ? "Month: Flexible" : m}</option>)}
            </select>
          </div>
          <textarea data-testid="request-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything to adjust? Dates, budget, travellers… (optional)" rows={2} className="w-full rounded-xl bg-[#F7F5F0] border border-[#E5E2D9] px-4 py-2.5 text-sm focus:outline-none focus:border-[#89937C]" />
          <button data-testid="request-submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors disabled:opacity-60">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send booking request
          </button>
        </form>
      )}
    </div>
  );
};

const fmt = (n, cur) => {
  if (n == null) return "—";
  const sym = (cur || "").toUpperCase().includes("INR") || (cur || "").includes("₹") ? "₹" : "$";
  return `${sym}${Number(n).toLocaleString(sym === "₹" ? "en-IN" : "en-US")}`;
};

export const JourneyPlanView = ({ plan, journeyId }) => {
  const [showRequest, setShowRequest] = useState(false);
  const share = () => {
    const url = `${window.location.origin}/journey/${journeyId}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Journey link copied — share it anywhere"));
  };
  const b = plan.budget || {};
  return (
    <div data-testid="journey-plan" className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <div className="overline mb-3">Your Personalized India Journey</div>
        <h1 data-testid="journey-title" className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#2D372B] leading-[1.05]">
          {plan.title}
        </h1>
        {plan.tagline && <p className="mt-4 text-lg italic text-[#AD6A48] font-serif-display">{plan.tagline}</p>}
        <p className="mt-5 text-[#5C665A] leading-relaxed max-w-2xl mx-auto">{plan.summary}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {(plan.destinations || []).map((d) => (
            <span key={d.name} className="inline-flex items-center gap-1.5 rounded-full border border-[#89937C] bg-[#89937C]/10 px-4 py-1.5 text-sm text-[#2D372B]">
              <MapPin size={13} className="text-[#89937C]" /> {d.name}{d.nights ? ` · ${d.nights}n` : ""}
            </span>
          ))}
          {plan.best_season && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E2D9] bg-white px-4 py-1.5 text-sm text-[#5C665A]">
              <CalendarDays size={13} className="text-[#AD6A48]" /> Best: {plan.best_season}
            </span>
          )}
        </div>
        <div className="mt-6 flex items-center justify-center gap-3 print:hidden">
          <button data-testid="journey-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#2D372B]/20 text-[#2D372B] text-sm font-medium hover:border-[#2D372B] transition-colors">
            <Printer size={14} /> Save as PDF
          </button>
          <button data-testid="journey-share-btn" onClick={share} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#2D372B]/20 text-[#2D372B] text-sm font-medium hover:border-[#2D372B] transition-colors">
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {(plan.days || []).map((d) => (
          <div key={d.day} data-testid="journey-day-card" className="bg-white border border-[#E5E2D9] rounded-3xl p-6 sm:p-8">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
              <div>
                <span className="overline">Day {d.day}{d.location ? ` — ${d.location}` : ""}</span>
                <h3 className="font-serif-display text-2xl text-[#2D372B] mt-1">{d.theme}</h3>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              {[["Morning", d.morning, Sun], ["Afternoon", d.afternoon, Sunset], ["Evening", d.evening, Moon]].map(([label, txt, Icon]) => (
                <div key={label} className="rounded-2xl bg-[#F7F5F0] p-4">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#89937C] font-semibold mb-1.5"><Icon size={13} /> {label}</div>
                  <p className="text-[#2D372B] leading-relaxed">{txt}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              {d.coffee_moment && (
                <div className="flex items-start gap-2.5 rounded-2xl border border-[#C57B57]/30 bg-[#C57B57]/5 p-4">
                  <Coffee size={15} className="text-[#AD6A48] mt-0.5 shrink-0" />
                  <div><span className="text-xs uppercase tracking-wider text-[#AD6A48] font-semibold block mb-0.5">Coffee moment</span><span className="text-[#2D372B]">{d.coffee_moment}</span></div>
                </div>
              )}
              {d.wellness_note && (
                <div className="flex items-start gap-2.5 rounded-2xl border border-[#89937C]/30 bg-[#89937C]/5 p-4">
                  <Leaf size={15} className="text-[#89937C] mt-0.5 shrink-0" />
                  <div><span className="text-xs uppercase tracking-wider text-[#89937C] font-semibold block mb-0.5">Wellness note</span><span className="text-[#2D372B]">{d.wellness_note}</span></div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <div className="bg-[#2D372B] rounded-3xl p-7 text-[#F7F5F0]">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A9B39D] font-semibold mb-4">Estimated Budget</div>
          {[["Accommodation", b.accommodation], ["Experiences", b.experiences], ["Food", b.food], ["Transport", b.transport]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-sm py-1.5 border-b border-white/10"><span className="text-[#C9CFC0]">{l}</span><span>{fmt(v, b.currency)}</span></div>
          ))}
          <div className="flex justify-between pt-3 font-serif-display text-2xl"><span>Total</span><span data-testid="journey-budget-total">{fmt(b.total, b.currency)}</span></div>
          <p className="text-xs text-[#A9B39D] mt-3">Indicative estimate — refined during concierge planning.</p>
        </div>
        <div className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
          <div className="overline mb-3">Prepare & Pack</div>
          <ul className="space-y-2 text-sm text-[#2D372B]">
            {[...(plan.prep_tips || []), ...(plan.packing || [])].map((t, i) => (
              <li key={i} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#89937C] mt-1.5 shrink-0" />{t}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-10 bg-[#C57B57]/10 border border-[#C57B57]/30 rounded-3xl p-8 text-center print:hidden">
        <h3 className="font-serif-display text-2xl text-[#2D372B]">Ready to make this real?</h3>
        <p className="text-sm text-[#5C665A] mt-2 max-w-md mx-auto">Coach Sanjay personally arranges every journey — stays, guides, transfers, training and the best coffee stops on your route.</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button data-testid="request-journey-btn" onClick={() => setShowRequest((s) => !s)} className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
            <Send size={15} /> Request this journey
          </button>
          <a href="/coaching#contact" data-testid="journey-contact-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3 border border-[#2D372B]/25 text-[#2D372B] font-medium hover:border-[#2D372B] transition-colors">
            Talk to Coach Sanjay <ArrowUpRight size={16} />
          </a>
          <Link to="/coffee/customize" data-testid="journey-coffee-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3 border border-[#AD6A48] text-[#AD6A48] font-medium hover:bg-[#AD6A48] hover:text-white transition-colors">
            <Coffee size={15} /> Taste our coffee first
          </Link>
        </div>
        {showRequest && <RequestJourneyPanel plan={plan} journeyId={journeyId} onClose={() => setShowRequest(false)} />}
      </div>
    </div>
  );
};
